<template>
  <div class="plans-wrapper">
    <div class="intro-wrapper">
      <div class="period-switch-container m-auto text-center">
        <h1 class="header font-outfit-bold text-center">
          {{ $t('pages.pricing.simpleAndAffordablePricePlans') }}
        </h1>

        <p class="subheader font-outfit-light mt-0">
          {{ $t('pages.pricing.allPlansComeWithA15DayMoneyBackGuarantee') }}
        </p>

        <div class="switch-wrapper mx-auto position-relative">
          <form-switcher
            v-model="period"
            size="md"
            variant="blue"
            :disabled="loading"
            :options="periodOptions"
          />
          <div
            class="arrow-text d-flex font-tragic-marker align-items-start"
            :class="arrowTextClass"
          >
            <svg width="40" height="29" viewBox="0 0 40 29" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M17.578 5.489c.085.375.06.768-.071 1.13-.21.602-.727.71-1.253.355-.254-.17-.51-.367-.796-.456-1.46-.452-2.937-.872-4.403-1.31-1.897-.573-3.804-1.114-5.802-1.216-.159-.007-.318.003-.516.005.054.128.072.188.102.241 3.205 5.46 7.02 10.426 11.784 14.62 3.197 2.812 6.757 4.953 10.957 5.916 2.096.482 4.199.794 6.355.477 1.083-.159 1.997-.589 2.735-1.44.33-.368.74-.654 1.2-.832.73-.28 1.2-.053 1.484.673.21.544.303 1.137.453 1.702.083.308-.02.516-.258.723a9.616 9.616 0 01-3.428 1.929c-2.35.739-4.712.628-7.074.084-4.756-1.095-8.838-3.491-12.568-6.55-4.05-3.322-7.432-7.257-10.427-11.54l-.684-.971a.733.733 0 00-.023.464c.231.87.411 1.756.716 2.6a353.125 353.125 0 002.626 6.917c.305.786.061 1.52-.13 2.262-.044.175-.221.317-.381.534-.225-.24-.384-.41-.534-.585-1.481-1.727-2.368-3.77-3.084-5.897-.92-2.749-1.646-5.566-2.877-8.207a16.235 16.235 0 01-.953-2.521 9.538 9.538 0 01-.37-2.42C.35 1.235.942.679 1.878.519c.245-.038.492-.06.74-.067 2.296-.076 4.479.5 6.653 1.136 2.42.707 4.845 1.393 7.264 2.098.122.036.279.109.329.21.262.517.556 1.036.713 1.593z" fill="currentColor"/></svg>
            <p>{{ arrowText }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="cards-wrapper text-center mt-lg-0">
      <el-row :gutter="40" class="cards-container d-flex flex-wrap m-auto text-center justify-content-center">
        <el-col
          v-for="plan in plans"
          v-show="plan.show"
          :key="plan.title"
          :md="8"
          :class="{ 'mt-md-3 mt-lg-0': !plan.popular }"
          class="mb-lg-0"
        >
          <plan-card
            v-model="cardValues[period][plan.slug]"
            class="plan-card mb-md-0"
            :title="plan.title"
            :plan="getPlan(plan.slug)"
            :popular="plan.popular"
            :description="plan.description"
            :price="getPlan(plan.slug) ? getPlan(plan.slug).price : null"
            :monthly-price="getMonthlyPrice(plan.slug)"
            :currency-symbol="getPlan(plan.slug) ? getPlan(plan.slug).currency : null"
            :period="period"
            :options="plansOptions"
            :loading="loading"
            :button-loading="plan.slug === loadingPlanSlug"
            :disabled-features="plan.disabledFeatures"
            @choose-plan="value => onChoosePlan(plan.slug, value)"
            @contact="onContact"
          />
        </el-col>

        <el-col :md="8" class="mb-lg-0 mt-md-3 mt-lg-0">
          <white-label-card
            class="plan-card mb-md-0"
            :period="period"
            @contact="onWhiteLabelContact"
          />
        </el-col>
      </el-row>
      <p class="prices-info">
        {{ $t('pages.pricing.pricesMayBeSubjectToAdditionalVatDependingOnYourCountry') }} 👆
      </p>
    </div>

    <!-- Dialogs -->
    <dialog-enterprise
      :enterprise-visible="dialogEnterpriseVisible"
      :type="contactType"
      @close-enterprise="dialogEnterpriseVisible = false"
    />

    <dialog-confirm
      ref="dialogConfirm"
      :visible="dialogConfirm"
      :preview-data="previewData"
      :record="chosenData"
      @close-dialog="dialogConfirm = false"
      @confirm-dialog="confirmedUpdatePlan"
    />

    <dialog-thank-you
      :dialog-thank-you-visible="dialogThankYouVisible"
      :thank-you-data="chosenData"
      @close-dialog="closeDialog"
    />
    <!-- /Dialogs -->
  </div>
</template>

<script>
import DialogConfirm from '@/views/Payment/Dialogs/DialogConfirm'
import DialogEnterprise from '@/views/Payment/Dialogs/DialogEnterprise'
import DialogThankYou from '@/views/Payment/Dialogs/DialogThankYou'
import FormSwitcher from '@/components/_v2/Form/FormSwitcher'
import MixinPaddle from '@/mixins/paddle'
import MixinPartnero from '@/mixins/partnero'
import PlanCard from '@/views/Payment/Components/Sections/Plans/PlanCard'
import WhiteLabelCard from '@/views/Payment/Components/Sections/Plans/WhiteLabelCard'
import { mapGetters } from 'vuex'
import { paymentQueryHelper } from '@/helpers/paymentQueryHelper'

import { formatPrice, getPriceInEuro } from '@/helpers/currencyHelper'

export default {
  name: 'PlansSection',

  components: {
    DialogConfirm,
    DialogEnterprise,
    DialogThankYou,
    FormSwitcher,
    PlanCard,
    WhiteLabelCard
  },

  mixins: [
    MixinPaddle,
    MixinPartnero
  ],

  data () {
    return {
      loading: false,
      updatedPrices: false,
      loadingPlanSlug: null,
      dialogConfirm: false,
      dialogEnterpriseVisible: false,
      contactType: 'enterprise',
      dialogThankYouVisible: false,
      previewData: {},
      chosenData: {},
      cardValues: {
        monthly: {
          Dreamer: 1,
          Founder: 1,
          TeamPro: 10
        },
        annual: {
          Dreamer: 1,
          Founder: 1,
          TeamPro: 10
        },
        lifetime: {
          Dreamer: 1,
          Founder: 1,
          TeamPro: 10
        }
      },
      period: 'annual',
      periodOptions: [
        {
          title: this.$t('pages.pricing.monthly'),
          value: 'monthly'
        },
        {
          title: this.$t('pages.pricing.annual'),
          value: 'annual'
        }
        // {
        //   title: this.$t('pages.pricing.lifetime'),
        //   value: 'lifetime'
        // }
      ],
      allPlans: [],
      plansOptions: [1, 3, 10, 30]
    }
  },

  computed: {
    ...mapGetters('user', [
      'isSubscribed',
      'getCurrentUser'
    ]),
    ...mapGetters('app', ['getRedirectRouteAfterPayment']),

    arrowText () {
      const texts = {
        monthly: this.$t('pages.pricing.arrowTexts.monthly'),
        annual: this.$t('pages.pricing.arrowTexts.annual'),
        lifetime: this.$t('pages.pricing.arrowTexts.lifetime')
      }

      return texts[this.period]
    },
    arrowTextClass () {
      return `arrow-text-${this.period}`
    },

    isLifetime () {
      return this.period === 'lifetime'
    },

    plans () {
      const plans = [
        {
          title: 'Dreamer',
          slug: 'Dreamer',
          description: this.$t('pages.pricing.plansDescriptions.dreamer'),
          popular: false,
          price: 55,
          disabledFeatures: [10, 11, 12],
          show: true
        },
        {
          title: 'Founder',
          slug: 'Founder',
          description: this.$t('pages.pricing.plansDescriptions.founder'),
          popular: true,
          price: 95,
          disabledFeatures: [12],
          show: true
        }
        // , {
        //   title: 'Team Pro',
        //   slug: 'TeamPro',
        //   description: this.$t('pages.pricing.plansDescriptions.teamPro'),
        //   popular: false,
        //   price: 1,
        //   disabledFeatures: null
        // }
      ]

      if (this.isLifetime) {
        plans[0].show = false
      }

      return plans
    }
  },

  watch: {
    updatedPrices: {
      handler (newValue) {
        if (newValue) {
          const planInfo = paymentQueryHelper(false, this.$route.query)
          if (planInfo && planInfo.type) {
            this.period = planInfo.type
            const plan = this.capitalizeFirstLetter(planInfo.plan)
            this.cardValues[this.period][plan] = planInfo.noi
          }

          // Open dialog for checkout
          if (planInfo) {
            const foundPlan = this.findPlan(planInfo.plan, this.period, planInfo.noi)
            this.choosePlan(foundPlan)
          }
        }
      },
      immediate: true
    }
  },

  created () {
    this.loading = true
    this.setupPaddle(this.getPlans)
  },

  methods: {
    getPlans () {
      this.$http.get('provider/plan').then(response => {
        this.allPlans = response.data.payload

        this.updatePlansPrices()
      })
    },
    onChoosePlan (slug, val) {
      const ideas = this.cardValues[this.period][slug]
      const foundPlan = this.findPlan(slug, this.period, ideas)

      this.choosePlan(foundPlan)
    },
    updatePlansPrices () {
      const plansPrices = []

      this.allPlans.forEach(plan => {
        // eslint-disable-next-line no-undef
        Paddle.Product.Prices(plan.plan_id, (prices) => {
          plansPrices.push(prices)
          const { price, currency } = formatPrice(prices.price.net)

          plan.price = price
          plan.currency = currency

          if (plansPrices.length === this.allPlans.length) {
            this.loading = false
            this.updatedPrices = true
          }
        })
      })
    },
    onContact () {
      this.contactType = 'enterprise'
      this.dialogEnterpriseVisible = true
    },
    onWhiteLabelContact () {
      this.contactType = 'whitelabel'
      this.dialogEnterpriseVisible = true
    },
    choosePlan (plan) {
      this.loadingPlanSlug = plan.slug
      this.selectedPlan = plan
      if (this.isSubscribed &&
        this.$store.state.user.subscription.status !== 'deleted' &&
        !this.$store.state.user.freeTrial) {
        this.triggerPayment(plan)

        return
      }

      this.triggerPaddle(plan)
    },
    getPlan (planSlug, period) {
      if (planSlug === 'Team') {
        return null
      }

      if (!period) period = this.period

      const ideas = this.cardValues[this.period][planSlug]
      const foundPlan = this.findPlan(planSlug, period, ideas)

      if (foundPlan) {
        return {
          currency: foundPlan.currency,
          price: this.period === 'annual' ? Math.round((foundPlan.price / 12)) : Math.round(foundPlan.price),
          aiRequests: foundPlan.ai_requests
        }
      }

      return null
    },
    getMonthlyPrice (planSlug) {
      const ideas = this.cardValues[this.period][planSlug]
      const foundPlan = this.findPlan(planSlug, 'monthly', ideas)

      return foundPlan ? foundPlan.price : null
    },
    findPlan (planSlug, period, ideas) {
      const periodTypes = {
        monthly: 'month',
        annual: 'year',
        lifetime: 'lifetime'
      }
      const type = periodTypes[period]

      return this.allPlans.find(plan => plan.slug.toLowerCase() === planSlug.toLowerCase() && plan.ideas === ideas && plan.type === type)
    },
    triggerPayment (plan) {
      if (this.$store.state.user.subscription.id === plan.id) {
        this.$notify({ message: 'You already have this plan!', type: 'error' })
        this.loadingPlanSlug = null
      } else {
        this.$http.post(
          'provider/update-plan',
          { ...plan, confirmed: this.confirmed }
        ).then((response) => {
          if (response.status === 200) {
            this.previewData = response.data.payload
            this.dialogConfirm = true
            this.loadingPlanSlug = null
            this.chosenData = Object.assign({}, plan)

            if (this.confirmed) {
              this.$refs.dialogConfirm.loading = false
              this.$store.commit('user/setSubscription', response.data.payload)
              this.$intercom.trackEvent('Updated subscription')
              this.dialogThankYouVisible = true
              this.dialogConfirm = false
            }

            if (!this.previewData.success) {
              this.previewData = {}
              this.dialogConfirm = false
              this.triggerPaddle(plan)
            }
          }
        })
      }
    },
    triggerPaddle (plan) {
      if (this.$store.state.user.subscription.status !== 'deleted' && this.$store.state.user.subscription.id === plan.id) {
        this.$notify({ message: 'You already have this plan!', type: 'error' })
        this.loadingPlanSlug = null
      } else {
        const userPlanInfo = plan.slug + plan.ideas + (plan.type === 'month' ? 'Monthly' : 'Annual')
        const startUserPlan = userPlanInfo + 'Start'

        this.$gtm.trackEvent({
          event: startUserPlan
        })

        this.$intercom.update({
          CheckoutStart: true
        })

        const $this = this
        const referral = window.Rewardful && window.Rewardful.referral
        this.createPartneroCustomer(this.getCurrentUser)

        // eslint-disable-next-line no-undef
        Paddle.Checkout.open({
          product: plan.plan_id,
          email: this.$store.state.user.email,
          customData: { customer_key: this.$store.state.user.email },
          passthrough: JSON.stringify({ userId: this.$store.state.user.id, rewardful: { referral: referral } }),
          locale: localStorage.getItem('lang'),
          successCallback: (data) => {
            this.$http.post('provider/select-plan', plan).then((response) => {
              if (response.status === 200) {
                // Send GTM events
                const successCheckout = 'checkout' + userPlanInfo + 'Success'
                $this.$gtm.trackEvent({
                  event: successCheckout
                })
                // Send GTM Purchase event
                $this.$gtm.trackEvent({
                  event: 'Purchase (New)',
                  value: getPriceInEuro([plan.type, plan.slug, plan.ideas, plan.currency]),
                  email: this.$store.state.user.email,
                  phone: 'Phone',
                  address: 'Address'
                })

                this.$intercom.update({
                  PlanType: `${plan.type.charAt(0).toUpperCase()}${plan.type.slice(1)}`,
                  PlanName: plan.slug,
                  PlanExpiry: response.data.payload.subscription.nextBillDate,
                  MaxIdeas: response.data.payload.subscription.ideas,
                  subscription_updated_at: this.$moment().unix()
                })
                this.$intercom.trackEvent('New subscription')

                this.$store.commit('user/setSubscription', response.data.payload)
                this.chosenData = Object.assign({}, plan)
                this.dialogThankYouVisible = true
              }
            })
          },
          loadCallback: () => {
            this.loadingPlanSlug = null
          },
          closeCallback: function (data) {
            this.loadingPlanSlug = null
            // Send GTM events
            const closeCheckout = 'checkout' + userPlanInfo + 'Close'
            $this.$gtm.trackEvent({
              event: closeCheckout
            })
          }
        })
      }
    },
    formatPrice (price) {
      return price.toString()
        .slice(0, -3)
        .replace(/\D/g, '')
    },
    confirmedUpdatePlan () {
      this.$refs.dialogConfirm.loading = true
      this.confirmed = true
      this.triggerPayment(this.selectedPlan)
    },
    closeDialog () {
      this.dialogThankYouVisible = false
      this.$router.push({ name: this.getRedirectRouteAfterPayment })
    },
    capitalizeFirstLetter (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
  }
}
</script>

<style scoped lang="scss">
.plans-wrapper {
  .intro-wrapper {
    padding-top: 50px;
    background-color: #F8FAFF;
    background-image: url('~@/assets/img/pricing/plans/waves.svg');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: bottom;
  }

  .period-switch-container {
    max-width: 1140px;
    padding: 0 20px 50px;

    @include media-breakpoint-up($md) {
      padding-bottom: 105px;
    }

    @include media-breakpoint-up($lg) {
      padding-bottom: 111px;
    }

    .header {
      margin: 0 auto 30px;
      font-size: 38px;
      max-width: 700px;

      @include media-breakpoint-up($md) {
        font-size: 52px;
      }
    }

    .subheader {
      max-width: 550px;
      margin: 0 auto 30px auto;
      font-size: 18px;

        @include media-breakpoint-up($md) {
          font-size: 24px;
      }
    }

    .switch-wrapper {
      max-width: 224px;

      .arrow-text {
        position: absolute;
        right: 0;
        margin-top: 18px;
        white-space: nowrap;
        color: var(--color-primary);

        @include media-breakpoint-up($md) {
          right: -45px;
          margin-top: 12px;
        }

        &-monthly {
          left: 20%;
          right: unset;
        }

        &-monthly,
        &-annual,
        &-lifetime {
          img, svg {
            transform: rotate(25deg);

            @include media-breakpoint-up($md) {
              transform: unset;
            }
          }
        }

        &-annual,
        &-lifetime {
          flex-direction: row-reverse;

          img, svg {
            transform: scaleX(-1) rotate(25deg);

            @include media-breakpoint-up($md) {
              transform: scaleX(-1);
            }
          }
        }

        &-annual {
          right: 30%;
        }

        &-lifetime {
          right: 15%;
        }

        p {
          color: var(--color-primary);
          font-size: 20px;
          padding-top: 9px;

          @include media-breakpoint-up($md) {
            font-size: 24px;
            margin: 3px 11px 0;
          }
        }
      }

      ::v-deep {
        .form-switch-wrapper {
          background: $color-white;
          border: 1px solid #bbc9e4;
          border-radius: 48px;
          font-size: 12px;
          text-transform: uppercase;
        }

        .switch-input.active + .switch-label.form-switcher-blue {
          color: white;
        }

        .switch-label {
          font-family: $outfit-medium;
          color: #081E4A;
        }
      }
    }
  }

  .cards-wrapper {
    margin-top: 70px;

    @include media-breakpoint-up($md) {
      margin-top: 20px;
    }

    .prices-info {
      margin-top: 10px;

      @include media-breakpoint-up($lg) {
        margin-top: 50px
      }
    }

    .cards-container {
      max-width: 1140px;
      padding: 0 13px;

      .el-col {
        margin-bottom: 20px;
      }

      ::v-deep {
        @include media-breakpoint-between($md, $lg) {
          .card-wrapper.popular {
            .plan-title {
              margin-top: 16px;
            }
          }
        }
      }
    }
  }
}
</style>
